import axios from "../../utils/axios";
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  usageReport: [],
  invoiceReport: [],
  orderReport: [],
  portfolioActivityReport: [],
  reportTemplates: [],
  reportHistory: [],
  invoiceListReport: [],
  customerSessionsReport: [],
  customerSimOverviewReport: [],
  customerSimListReport: [],
  timeoutReports: [],
  timeoutReportLines: [],
  timeoutReportColumns: [],
};

const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    setUsageReport(state, action) {
      state.usageReport = action.payload;
    },
    setInvoiceReport(state, action) {
      state.invoiceReport = action.payload;
    },
    setOrderReport(state, action) {
      state.orderReport = action.payload;
    },
    setPortfolioActivityReport(state, action) {
      state.portfolioActivityReport = action.payload;
    },
    setInvoiceListReport(state, action) {
      state.invoiceListReport = action.payload;
    },
    setCustomerSessionsReport(state, action) {
      state.customerSessionsReport = action.payload;
    },
    setCustomerSimOverviewReport(state, action) {
      state.customerSimOverviewReport = action.payload;
    },
    setCustomerSimListReport(state, action) {
      state.customerSimListReport = action.payload;
    },
    setReportTemplates(state, action) {
      state.reportTemplates = action.payload;
    },
    addReportTemplate(state, action) {
      const { newReportTemplate } = action.payload;
      return {
        ...state,
        reportTemplates: [...state.reportTemplates, newReportTemplate],
      };
    },
    updateReportTemplate(state, action) {
      const { id, updatedReportTemplate } = action.payload;
      return {
        ...state,
        reportTemplates: state.reportTemplates.map((reportTemplate) =>
          reportTemplate.id === id ? updatedReportTemplate : reportTemplate
        ),
      };
    },
    removeReportTemplate(state, action) {
      const { id } = action.payload;
      return {
        ...state,
        reportTemplates: state.reportTemplates.filter(
          (reportTemplate) => reportTemplate.id !== id
        ),
      };
    },
    setReportHistory(state, action) {
      state.reportHistory = action.payload;
    },
    deleteReportHistory(state, action) {
      const { id } = action.payload;
      return {
        ...state,
        reportHistory: state.reportHistory.filter((report) => report.id !== id),
      };
    },
    setTimeoutReports(state, action) {
      state.timeoutReports = action.payload;
    },
    addTimeoutReport(state, action) {
      const { newTimeoutReport } = action.payload;
      return {
        ...state,
        timeoutReports: [...state.timeoutReports, newTimeoutReport],
      };
    },
    removeTimeoutReport(state, action) {
      const { id } = action.payload;
      return {
        ...state,
        timeoutReports: state.timeoutReports.filter(
          (report) => report.id !== id
        ),
      };
    },
    setTimeoutReportLines(state, action) {
      state.timeoutReportLines = action.payload;
    },
    setTimeoutReportColumns(state, action) {
      state.timeoutReportColumns = action.payload;
    },
    updateTimeoutReportStatus(state, action) {
      const { id, reportDetails } = action.payload;
      return {
        ...state,
        timeoutReports: state.timeoutReports.map((timeoutReport) =>
          timeoutReport.id === id
            ? {
                ...timeoutReport,
                status: reportDetails.status,
                finalizedAt: reportDetails.finalizedAt,
              }
            : timeoutReport
        ),
      };
    },
  },
});

export const getUserReports = () => {
  return async (dispatch) => {
    const impersonationToken =
      window.localStorage.getItem("impersonationToken");
    const accessToken = window.localStorage.getItem("accessToken");
    if (!accessToken) return Promise.reject("No access token");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      dispatch(reportsSlice.actions.setReportHistory(response.data.data));
      return Promise.resolve(response.data.message);
    } catch (error) {
      return Promise.reject(error.message);
    }
  };
};

export function deleteReportFromHistory({ id }) {
  return async (dispatch) => {
    const impersonationToken =
      window.localStorage.getItem("impersonationToken");
    const accessToken = window.localStorage.getItem("accessToken");
    if (!accessToken) return Promise.reject("No access token");
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports/${id}`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      dispatch(reportsSlice.actions.deleteReportHistory({ id }));
      return Promise.resolve(response.data.message);
    } catch (error) {
      return Promise.reject(error.message);
    }
  };
}

export const getUsageReport = ({
  usageType,
  groupByField,
  groupByPeriod,
  usageStartDate,
  usageEndDate,
  userId,
  simSearchCriteria,
  imsi,
  startImsi,
  endImsi,
  iccid,
}) => {
  return async (dispatch) => {
    const impersonationToken = localStorage.getItem("impersonationToken");
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      return Promise.reject("No access token");
    }
    try {
      const padZero = (num) => num.toString().padStart(2, "0");

      const start = new Date(usageStartDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(usageEndDate);
      end.setHours(23, 59, 59, 999);
      // Format the date to yyyy-MM-ddTHH:mm:ss
      const formattedUsageStartDate = `${start.getFullYear()}-${padZero(
        start.getMonth() + 1
      )}-${padZero(start.getDate())}T${padZero(start.getHours())}:${padZero(
        start.getMinutes()
      )}:${padZero(start.getSeconds())}`;

      // Format the end date to yyyy-MM-ddTHH:mm:ss
      const formattedUsageEndDate = `${end.getFullYear()}-${padZero(
        end.getMonth() + 1
      )}-${padZero(end.getDate())}T${padZero(end.getHours())}:${padZero(
        end.getMinutes()
      )}:${padZero(end.getSeconds())}`;

      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports/usage?usageType=${usageType}&groupByField=${groupByField}&groupByPeriod=${groupByPeriod}&usageStartDate=${formattedUsageStartDate}&usageEndDate=${formattedUsageEndDate}&simSearchCriteria=${simSearchCriteria}&imsi=${imsi}&startImsi=${startImsi}&endImsi=${endImsi}&iccid=${iccid}&userId=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Impersonation: impersonationToken,
          },
        }
      );
      if (response.status === 202) {
        dispatch(
          reportsSlice.actions.addTimeoutReport({
            newTimeoutReport: response.data.data,
          })
        );
        return Promise.reject(
          "Report timed out. A deferred report was created."
        );
      }
      dispatch(reportsSlice.actions.setUsageReport(response.data.data));
      return Promise.resolve(response.data.message);
    } catch (error) {
      const status = error.status;
      const message = error.message || "An unknown error occurred.";
      if (status === 500) {
        return Promise.reject("Internal server error. Please try again later.");
      }

      // Handle other errors (e.g. network, unauthorized, etc.)
      return Promise.reject(message);
    }
  };
};

export const getInvoiceReport = ({ invoiceId }) => {
  return async (dispatch) => {
    const impersonationToken = localStorage.getItem("impersonationToken");
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      return Promise.reject("No access token");
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports/invoice?invoiceId=${invoiceId}`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      if (response.status === 202) {
        dispatch(
          reportsSlice.actions.addTimeoutReport({
            newTimeoutReport: response.data.data,
          })
        );
        return Promise.reject(
          "Report timed out. A deferred report was created."
        );
      }
      dispatch(reportsSlice.actions.setInvoiceReport(response.data.data));
      return Promise.resolve(response.data.message);
    } catch (error) {
      const status = error.status;
      const message = error.message || "An unknown error occurred.";
      if (status === 500) {
        return Promise.reject("Internal server error. Please try again later.");
      }

      // Handle other errors (e.g. network, unauthorized, etc.)
      return Promise.reject(message);
    }
  };
};

export const getInvoiceListReport = ({ date }) => {
  return async (dispatch) => {
    const impersonationToken = localStorage.getItem("impersonationToken");
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      return Promise.reject("No access token");
    }
    try {
      const padZero = (num) => num.toString().padStart(2, "0");
      const formattedInvoiceStartDate = `${date.getFullYear()}-${padZero(
        date.getMonth() + 1
      )}-${padZero(date.getDate())}T${padZero(date.getHours())}:${padZero(
        date.getMinutes()
      )}:${padZero(date.getSeconds())}`;
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports/invoice-list?date=${formattedInvoiceStartDate}`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      if (response.status === 202) {
        dispatch(
          reportsSlice.actions.addTimeoutReport({
            newTimeoutReport: response.data.data,
          })
        );
        return Promise.reject(
          "Report timed out. A deferred report was created."
        );
      }
      dispatch(reportsSlice.actions.setInvoiceListReport(response.data.data));
      return Promise.resolve(response.data.message);
    } catch (error) {
      const status = error.status;
      const message = error.message || "An unknown error occurred.";
      if (status === 500) {
        return Promise.reject("Internal server error. Please try again later.");
      }

      // Handle other errors (e.g. network, unauthorized, etc.)
      return Promise.reject(message);
    }
  };
};

export const getOrderReport = ({ startDate, endDate }) => {
  return async (dispatch) => {
    const impersonationToken = localStorage.getItem("impersonationToken");
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      return Promise.reject("No access token");
    }
    try {
      const padZero = (num) => num.toString().padStart(2, "0");
      // Format the date to yyyy-MM-ddTHH:mm:ss
      const formattedStartDate = `${startDate.getFullYear()}-${padZero(
        startDate.getMonth() + 1
      )}-${padZero(startDate.getDate())}T${padZero(
        startDate.getHours()
      )}:${padZero(startDate.getMinutes())}:${padZero(startDate.getSeconds())}`;

      // Format the end date to yyyy-MM-ddTHH:mm:ss
      const formattedEndDate = `${endDate.getFullYear()}-${padZero(
        endDate.getMonth() + 1
      )}-${padZero(endDate.getDate())}T${padZero(endDate.getHours())}:${padZero(
        endDate.getMinutes()
      )}:${padZero(endDate.getSeconds())}`;
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports/orders?startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      if (response.status === 202) {
        dispatch(
          reportsSlice.actions.addTimeoutReport({
            newTimeoutReport: response.data.data,
          })
        );
        return Promise.reject(
          "Report timed out. A deferred report was created."
        );
      }
      dispatch(reportsSlice.actions.setOrderReport(response.data.data));
      return Promise.resolve(response.data.message);
    } catch (error) {
      const status = error.status;
      const message = error.message || "An unknown error occurred.";
      if (status === 500) {
        return Promise.reject("Internal server error. Please try again later.");
      }

      // Handle other errors (e.g. network, unauthorized, etc.)
      return Promise.reject(message);
    }
  };
};

export const getPortfolioActivityReport = () => {
  return async (dispatch) => {
    const impersonationToken = localStorage.getItem("impersonationToken");
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      return Promise.reject("No access token");
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports/portfolio-activity`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      if (response.status === 202) {
        dispatch(
          reportsSlice.actions.addTimeoutReport({
            newTimeoutReport: response.data.data,
          })
        );
        return Promise.reject(
          "Report timed out. A deferred report was created."
        );
      }
      dispatch(
        reportsSlice.actions.setPortfolioActivityReport(response.data.data)
      );
      return Promise.resolve(response.data.message);
    } catch (error) {
      const status = error.status;
      const message = error.message || "An unknown error occurred.";
      if (status === 500) {
        return Promise.reject("Internal server error. Please try again later.");
      }

      // Handle other errors (e.g. network, unauthorized, etc.)
      return Promise.reject(message);
    }
  };
};

export const getCustomerSessionsReport = () => {
  return async (dispatch) => {
    const impersonationToken = localStorage.getItem("impersonationToken");
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      return Promise.reject("No access token");
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports/customer-sessions`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      if (response.status === 202) {
        dispatch(
          reportsSlice.actions.addTimeoutReport({
            newTimeoutReport: response.data.data,
          })
        );
        return Promise.reject(
          "Report timed out. A deferred report was created."
        );
      }
      dispatch(
        reportsSlice.actions.setCustomerSessionsReport(response.data.data)
      );
      return Promise.resolve(response.data.message);
    } catch (error) {
      const status = error.status;
      const message = error.message || "An unknown error occurred.";
      if (status === 500) {
        return Promise.reject("Internal server error. Please try again later.");
      }

      // Handle other errors (e.g. network, unauthorized, etc.)
      return Promise.reject(message);
    }
  };
};

export const getCustomerSimOverviewReport = () => {
  return async (dispatch) => {
    const impersonationToken = localStorage.getItem("impersonationToken");
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      return Promise.reject("No access token");
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports/sim-overview`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      if (response.status === 202) {
        dispatch(
          reportsSlice.actions.addTimeoutReport({
            newTimeoutReport: response.data.data,
          })
        );
        return Promise.reject(
          "Report timed out. A deferred report was created."
        );
      }
      dispatch(
        reportsSlice.actions.setCustomerSimOverviewReport(response.data.data)
      );
      return Promise.resolve(response.data.message);
    } catch (error) {
      const status = error.status;
      const message = error.message || "An unknown error occurred.";
      if (status === 500) {
        return Promise.reject("Internal server error. Please try again later.");
      }

      // Handle other errors (e.g. network, unauthorized, etc.)
      return Promise.reject(message);
    }
  };
};

export const getAdminCustomerSimOverviewReport = ({ customerId }) => {
  return async (dispatch) => {
    const impersonationToken = localStorage.getItem("impersonationToken");
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      return Promise.reject("No access token");
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports/customer-sim-overview`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
          params: {
            customerId: customerId,
          },
        }
      );
      if (response.status === 202) {
        dispatch(
          reportsSlice.actions.addTimeoutReport({
            newTimeoutReport: response.data.data,
          })
        );
        return Promise.reject(
          "Report timed out. A deferred report was created."
        );
      }
      dispatch(
        reportsSlice.actions.setCustomerSimOverviewReport(response.data.data)
      );
      return Promise.resolve(response.data.message);
    } catch (error) {
      const status = error.status;
      const message = error.message || "An unknown error occurred.";
      if (status === 500) {
        return Promise.reject("Internal server error. Please try again later.");
      }

      // Handle other errors (e.g. network, unauthorized, etc.)
      return Promise.reject(message);
    }
  };
};

export const getCustomerSimListReport = () => {
  return async (dispatch) => {
    const impersonationToken = localStorage.getItem("impersonationToken");
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      return Promise.reject("No access token");
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports/sim-list`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      if (response.status === 202) {
        dispatch(
          reportsSlice.actions.addTimeoutReport({
            newTimeoutReport: response.data.data,
          })
        );
        return Promise.reject(
          "Report timed out. A deferred report was created."
        );
      }
      dispatch(
        reportsSlice.actions.setCustomerSimListReport(response.data.data)
      );
      return Promise.resolve(response.data.message);
    } catch (error) {
      const status = error.status;
      const message = error.message || "An unknown error occurred.";
      if (status === 500) {
        return Promise.reject("Internal server error. Please try again later.");
      }

      // Handle other errors (e.g. network, unauthorized, etc.)
      return Promise.reject(message);
    }
  };
};

export async function exportReport({ headers, data, format }) {
  try {
    const impersonationToken =
      window.localStorage.getItem("impersonationToken");
    const accessToken = window.localStorage.getItem("accessToken");
    if (!accessToken) return Promise.reject("No access token");
    const dataToSend = {
      headers,
      report: data,
      format,
    };
    const response = await axios.post(
      `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/reports/export`,
      dataToSend,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${
            impersonationToken ? impersonationToken : accessToken
          }`,
        },
      }
    );
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error.message);
  }
}

export async function exportTimeoutReport({ id, format }) {
  try {
    const impersonationToken =
      window.localStorage.getItem("impersonationToken");
    const accessToken = window.localStorage.getItem("accessToken");
    if (!accessToken) return Promise.reject("No access token");
    const data = {
      id: id,
      format: format,
    };
    const response = await axios.post(
      `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/timeout-reports/export`,
      data,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${
            impersonationToken ? impersonationToken : accessToken
          }`,
        },
      }
    );
    return Promise.resolve(response);
  } catch (error) {
    return Promise.reject(error.message);
  }
}

export function getReportTemplates() {
  return async (dispatch) => {
    const accessToken = window.localStorage.getItem("accessToken");
    const impersonationToken =
      window.localStorage.getItem("impersonationToken");
    if (!accessToken) return Promise.reject("No access token");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/report-templates`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      dispatch(reportsSlice.actions.setReportTemplates(response.data.data));
      return Promise.resolve(response.data.message);
    } catch (error) {
      return Promise.reject(error.message);
    }
  };
}

export function createReportTemplate({ data }) {
  return async (dispatch) => {
    const accessToken = window.localStorage.getItem("accessToken");
    const impersonationToken =
      window.localStorage.getItem("impersonationToken");
    if (!accessToken) return Promise.reject("No access token");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/report-templates`,
        data,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      dispatch(
        reportsSlice.actions.addReportTemplate({
          newReportTemplate: response.data.data,
        })
      );
      return Promise.resolve(response.data.message);
    } catch (error) {
      return Promise.reject(error.message);
    }
  };
}

export function editReportTemplate({ id, data }) {
  return async (dispatch) => {
    const accessToken = window.localStorage.getItem("accessToken");
    const impersonationToken =
      window.localStorage.getItem("impersonationToken");
    if (!accessToken) return Promise.reject("No access token");
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/report-templates/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      dispatch(
        reportsSlice.actions.updateReportTemplate({
          id,
          updatedReportTemplate: response.data.data,
        })
      );
      return Promise.resolve(response.data.message);
    } catch (error) {
      return Promise.reject(error.message);
    }
  };
}

export function deleteReportTemplate({ id }) {
  return async (dispatch) => {
    const accessToken = window.localStorage.getItem("accessToken");
    const impersonationToken =
      window.localStorage.getItem("impersonationToken");
    if (!accessToken) return Promise.reject("No access token");
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/report-templates/${id}`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      dispatch(reportsSlice.actions.removeReportTemplate({ id }));
      return Promise.resolve(response.data.message);
    } catch (error) {
      return Promise.reject(error.message);
    }
  };
}

export function getTimeoutReports() {
  return async (dispatch) => {
    const impersonationToken =
      window.localStorage.getItem("impersonationToken");
    const accessToken = window.localStorage.getItem("accessToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/timeout-reports`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      dispatch(reportsSlice.actions.setTimeoutReports(response.data.data));
      return Promise.resolve(response.data.message);
    } catch (error) {
      return Promise.reject(error.message);
    }
  };
}

export function getTimeoutReportLines({ id, page, pageSize }) {
  return async (dispatch) => {
    const impersonationToken =
      window.localStorage.getItem("impersonationToken");
    const accessToken = window.localStorage.getItem("accessToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/timeout-report-lines/${id}`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
          params: {
            page: page,
            pageSize: pageSize,
          },
        }
      );
      dispatch(
        reportsSlice.actions.setTimeoutReportColumns(
          createReactTableColumns(response.data.data.columns)
        )
      );
      dispatch(reportsSlice.actions.setTimeoutReportLines(response.data.data));
      return Promise.resolve(response.data.message);
    } catch (error) {
      return Promise.reject(error.message);
    }
  };
}

export function deleteTimeoutReport({ id }) {
  return async (dispatch) => {
    const impersonationToken =
      window.localStorage.getItem("impersonationToken");
    const accessToken = window.localStorage.getItem("accessToken");
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_POS2CLOUD_BACKEND_URL}/timeout-reports/${id}`,
        {
          headers: {
            Authorization: `Bearer ${
              impersonationToken ? impersonationToken : accessToken
            }`,
          },
        }
      );
      dispatch(reportsSlice.actions.removeTimeoutReport({ id }));
      return Promise.resolve(response.data.message);
    } catch (error) {
      return Promise.reject(error.message);
    }
  };
}

const createReactTableColumns = (columnNames) =>
  columnNames.map((name) => ({
    Header: name
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase()),
    accessor: name,
    minWidth: 120, // ensure enough width
    style: { whiteSpace: "nowrap" }, // prevent vertical wrapping
    Cell: ({ value }) => {
      if (Array.isArray(value)) {
        return value.map((cf, i) => (
          <div key={i}>
            {cf.name}: {cf.value}
          </div>
        ));
      }
      return value ?? "";
    },
  }));

export const { setReports, updateTimeoutReportStatus } = reportsSlice.actions;
export default reportsSlice.reducer;
